const extractDocText = (content: unknown[]): string => {
	const parts: string[] = [];
	const extract = (nodes: unknown[]) => {
		for (const node of nodes) {
			const n = node as Record<string, unknown>;
			if (typeof n.text === "string") parts.push(n.text);
			if (Array.isArray(n.content)) extract(n.content);
		}
	};
	extract(content);
	return parts.join(" ").trim();
};

const parseContent = (content: unknown): Record<string, unknown> | null => {
	if (!content) return null;
	try {
		return typeof content === "string"
			? JSON.parse(content)
			: (content as Record<string, unknown>);
	} catch {
		return null;
	}
};

const MESSAGE_TYPE_LABELS: Record<string, string> = {
	job_request_v1: "Neue Auftragsanfrage",
	job_request_custom_v1: "Individueller Auftrag",
	job_request_denied: "Auftragsanfrage abgelehnt",
	job_request_accepted: "Auftragsanfrage angenommen",
	connection_request: "Verbindungsanfrage",
	connection_request_accepted: "Verbindung hergestellt",
	connection_request_denied: "Verbindungsanfrage abgelehnt",
	shared_file: "Datei geteilt",
	shared_file_v2: "Datei geteilt",
	delivery_created: "Neue Lieferung",
	delivery_status_updated: "Lieferungsstatus aktualisiert",
};

const SENDER_AWARE_LABELS: Record<string, { self: string; other: string }> = {
	job_request_v1: {
		self: "Sie haben einen Auftrag erstellt",
		other: "Neue Auftragsanfrage erhalten",
	},
	job_request_custom_v1: {
		self: "Sie haben einen individuellen Auftrag erstellt",
		other: "Individueller Auftrag erhalten",
	},
	connection_request: {
		self: "Verbindungsanfrage gesendet",
		other: "Verbindungsanfrage erhalten",
	},
};

const truncate = (text: string, maxLen: number): string =>
	text.length > maxLen ? text.substring(0, maxLen) + "\u2026" : text;

const extractPlainText = (parsed: Record<string, unknown>, maxLen: number): string => {
	const type = parsed.type as string | undefined;

	if (type === "doc" && Array.isArray(parsed.content)) {
		const text = extractDocText(parsed.content);
		return text ? truncate(text, maxLen) : "";
	}

	const text =
		typeof parsed.text === "string"
			? parsed.text.replace(/<[^>]*>/g, "").trim()
			: "";
	return text ? truncate(text, maxLen) : "";
};

export const getMessagePreview = (
	content: unknown,
	opts?: { userId?: string; senderId?: string | null; maxLen?: number }
): string => {
	const maxLen = opts?.maxLen ?? 70;
	const parsed = parseContent(content);
	if (!parsed) {
		if (!content) return "";
		const str = String(content);
		return truncate(str, maxLen);
	}

	const type = parsed.type as string | undefined;
	if (!type) return extractPlainText(parsed, maxLen);

	const isSender = opts?.userId && opts.senderId === opts.userId;

	const senderAware = SENDER_AWARE_LABELS[type];
	if (senderAware) {
		const label = isSender ? senderAware.self : senderAware.other;
		return isSender ? label : label;
	}

	const label = MESSAGE_TYPE_LABELS[type];
	if (label) return label;

	const text = extractPlainText(parsed, maxLen);
	return isSender && text ? `Sie: ${text}` : text;
};

export const formatMessageTime = (date: string): string => {
	return new Date(date).toLocaleTimeString("de-CH", {
		hour: "2-digit",
		minute: "2-digit",
		timeZone: "Europe/Zurich",
	});
};

export const formatRelativeTime = (date: string | null): string => {
	if (!date) return "";
	const now = new Date();
	const past = new Date(date);
	const diffMs = now.getTime() - past.getTime();
	const diffHours = diffMs / (1000 * 60 * 60);

	if (diffHours < 24) return formatMessageTime(date);
	if (diffHours < 48) return "Gestern";
	if (diffHours < 7 * 24) {
		return new Intl.DateTimeFormat("de-DE", {
			weekday: "long",
			timeZone: "Europe/Zurich",
		}).format(past);
	}
	return past.toLocaleDateString("de-CH", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
	});
};
